/**
 * feat-299 — the page-level pieces of inline editing: the subtitle's mode hint
 * and the "waiting for approval" note (design NOTES §8 "Page-level note", §9).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { APPROVAL_EDITOR_COPY } from '../curriculum/approvalCopy';
import { QUIZ_REVIEW_EDIT_COPY as COPY } from './editCopy';
import { FOCUS } from './InlineEditable';
import { PendingState } from './pendingApprovals';
import { LanguageMode } from '../quizReviewLanguage';
import { APPROVAL_QUEUE_PATH } from './reviewEdit';

/**
 * Appended to the subtitle. The translated-only mode (`alt`, e.g. ES) has nothing editable, so it says how to get
 * there; EN and Both say what is editable — and, when the tenant's approval
 * gate is OFF, that a save reaches phones at once (NOTES §9).
 */
export const SubtitleEditHint: React.FC<{ mode: LanguageMode; pending: PendingState }> = ({
  mode,
  pending,
}) => {
  if (mode === 'alt') return <>{COPY.subtitleEsHint}</>;
  return (
    <>
      {COPY.subtitleEditHint}
      {pending.status === 'not-required' && <> {COPY.editsGoLive}</>}
    </>
  );
};

/**
 * Shown only when approval is required, the pending read succeeded, and at
 * least one Lesson is waiting. A failed read shows nothing (spec §5 / AT-13 —
 * a deliberate graceful degrade; the subtitle still states the rule).
 */
export const PendingNote: React.FC<{ pending: PendingState }> = ({ pending }) => {
  if (pending.status !== 'ready' || pending.lessonsPending <= 0) return null;
  return (
    <div
      className="mt-3 inline-flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-xl bg-white ring-1 ring-black/5 px-3 py-2 text-sm"
      data-testid="pending-note"
    >
      <span className="rounded-full bg-cream-dark ring-1 ring-black/5 px-2 py-[3px] text-[10px] font-bold leading-none text-inkMuted">
        <span aria-hidden>●</span>{' '}
        {APPROVAL_EDITOR_COPY.headerBadge(pending.lessonsPending, pending.changesPending)}
      </span>
      <span className="text-inkMuted">{COPY.editsWaitForApproval}</span>
      <Link
        to={APPROVAL_QUEUE_PATH}
        className={`font-semibold text-ink underline underline-offset-2 hover:no-underline rounded ${FOCUS}`}
      >
        {APPROVAL_EDITOR_COPY.queueLink}
      </Link>
    </div>
  );
};
